import React, { FC, useCallback } from 'react';
import { confirm, notification } from 'src/common';
import { useTranslation } from 'react-i18next';
import modalStyles from 'src/common/Modal/index.module.scss';
import { trackArchiveWorkspace, trackDeleteWorkspace, trackRestoreWorkspace } from 'src/segment/events';
import {
  useWorkspaces,
  useArchiveWorkspace,
  useDeleteWorkspace,
  useRestoreWorkspace,
  useExportWorkspaces
} from 'src/models/workspace/hooks';
import { QuickActions, QuickActionsItem, QuickActionsDivider } from 'src/shared';
import { useOpenSidebar } from 'src/shared/InfoSidebar/hooks';
import { SidebarMode } from 'src/shared/InfoSidebar/types';
import { faPen, faBoxArchive, faBoxOpen, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { ExportIcon } from 'src/common/icons';
import { useNavigate } from 'react-router';
import { Paths } from 'src/pages/paths';

export interface IProps {
  workspaceId: string;
  isActive: boolean;
}

export const WorkspaceQuickActions: FC<IProps> = props => {
  const { workspaceId, isActive } = props;
  const [deleteWS] = useDeleteWorkspace();
  const [[archiveWS], { enabled: archivingAllowed }] = useArchiveWorkspace();
  const [[restoreWS], { enabled: restoringAllowed }] = useRestoreWorkspace();
  const { exportWorkspaces, isExportInProgress, enabled: exportingAllowed } = useExportWorkspaces();
  const { refetch } = useWorkspaces(true);

  const { t } = useTranslation();
  const openWsPreview = useOpenSidebar();
  const navigate = useNavigate();

  const showDrawer = useCallback(() => {
    openWsPreview({ id: workspaceId, mode: SidebarMode.WORKSPACE_INFO });
  }, [openWsPreview, workspaceId]);

  const onRestoreWorkspace = useCallback(() => {
    restoreWS({
      variables: {
        id: workspaceId
      }
    })
      .then(() => {
        trackRestoreWorkspace({
          id: workspaceId
        });
      })
      .catch(() => {
        notification.error({
          description: t('Common.unknownErrorDesc'),
          message: t('Common.unknownError')
        });
      });
  }, [restoreWS, t, workspaceId]);

  const onArchiveWorkspace = useCallback(() => {
    confirm.confirm({
      title: t('BidSpaces.archiveBidSpace'),
      content: t('BidSpaces.archiveBidSpaceExplanation'),
      okText: t('Common.archive'),
      cancelText: t('Common.cancel'),
      className: modalStyles.confirmModal,
      centered: true,
      onOk() {
        archiveWS({
          variables: {
            id: workspaceId
          }
        })
          .then(() => {
            trackArchiveWorkspace({
              id: workspaceId
            });
          })
          .catch(() =>
            notification.error({
              description: t('Common.unknownErrorDesc'),
              message: t('Common.unknownError')
            })
          );
      },
      onCancel() {
        console.log('Cancel');
      }
    });
  }, [archiveWS, t, workspaceId]);

  const onDeleteWorkspace = useCallback(() => {
    confirm.confirm({
      title: t('BidSpaces.deleteBidSpace'),
      content: t('BidSpaces.deleteBidSpaceExplanation'),
      okText: t('Common.delete'),
      cancelText: t('Common.cancel'),
      className: modalStyles.confirmModal,
      centered: true,
      onOk() {
        deleteWS({
          variables: {
            id: workspaceId
          }
        })
          .then(() => refetch())
          .then(() => {
            navigate({
              pathname: Paths.BIDSPACES
            });
          })
          .then(() => {
            trackDeleteWorkspace({
              id: workspaceId
            });
          })
          .catch(() =>
            notification.error({
              description: t('Common.unknownErrorDesc'),
              message: t('Common.unknownError')
            })
          );
      },
      onCancel() {
        console.log('Cancel');
      }
    });
  }, [deleteWS, navigate, refetch, t, workspaceId]);

  const onExportWorkspace = useCallback(() => {
    exportWorkspaces([workspaceId]);
  }, [exportWorkspaces, workspaceId]);

  return (
    <QuickActions>
      <QuickActionsItem onClick={showDrawer} title={t('BidSpaces.editBidSpace')} key={'edit'} icon={faPen} />
      {archivingAllowed && isActive && (
        <QuickActionsItem
          onClick={onArchiveWorkspace}
          title={t('BidSpaces.archiveBidSpace')}
          key={'archive'}
          icon={faBoxArchive}
        />
      )}
      {exportingAllowed && isActive && (
        <QuickActionsItem
          onClick={onExportWorkspace}
          title={t('BidSpaces.exportBidSpace')}
          key={'export'}
          disabled={isExportInProgress([workspaceId])}
          CustomIcon={ExportIcon}
        />
      )}
      {restoringAllowed && !isActive && (
        <QuickActionsItem
          onClick={onRestoreWorkspace}
          title={t('BidSpaces.restoreBidSpace')}
          key={'restore'}
          icon={faBoxOpen}
        />
      )}
      {
        <>
          <QuickActionsDivider />
          <QuickActionsItem
            onClick={onDeleteWorkspace}
            title={t('BidSpaces.deleteBidSpace')}
            key={'delete'}
            icon={faTrashCan}
            danger
          />
        </>
      }
    </QuickActions>
  );
};

export default WorkspaceQuickActions;
